/**
 * THE MODULE CODES THE SCREEN SHOULD OFFER — the login token's `moduleCodes`, plus `MODIFICATIONS` for a selected correction
 * approver and for nobody else (see `core/permissions/modifications-access.ts`), and `DOWNLOAD` for a holder of `data-export:read`
 * (see `core/permissions/download-access.ts`). Use this, not `useAuthStore(...moduleCodes)`,
 * wherever a module or a section is OFFERED, so the Modifications module cannot be visible in one place and hidden in another.
 *
 * While the approver check is loading, or if it fails, the user is treated as NOT an approver: a door that is shown late is
 * better than one shown to someone it will refuse.
 */
import { useMemo } from 'react';
import { useAuthStore } from '@store/auth.store';
import { useCorrectionWorkflowMe } from '@features/billing/hooks/useCorrectionWorkflow';
import { withModificationsAccess } from '@core/permissions/modifications-access';
import { withDownloadAccess } from '@core/permissions/download-access';

export function useIsCorrectionApprover(): boolean {
  return useCorrectionWorkflowMe().data?.isApprover === true;
}

export function useEffectiveModuleCodes(): string[] {
  const moduleCodes = useAuthStore((state) => state.moduleCodes);
  const permissions = useAuthStore((state) => state.permissions);
  const isApprover = useIsCorrectionApprover();
  // Download is opened by the permission itself (see `core/permissions/download-access.ts`), Modifications by the approver selection.
  return useMemo(() => withDownloadAccess(withModificationsAccess(moduleCodes, isApprover), permissions), [moduleCodes, permissions, isApprover]);
}
